import { useState } from 'react'
import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import Fade from '@mui/material/Fade'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import ExclamationIcon from '@/core/assets/ExclamationIcon.svg?react'
import { useTranslate } from '@repo/unicity'

const DESCRIPTION_KEYS = [
    'Be_minimun_8_characters',
    'include_least_one_lowercase_letter',
    'include_least_one_uppercase_letter',
    'include_least_one_number',
    'include_least_one_special_character'
]

const Arrow = styled('span')`
    bottom: 5px;
    position: absolute;
    width: 20px;
    height: 10px;
`

const Triangle = styled('div')`
    content: '';
    box-sizing: border-box;
    position: absolute;
    width: 19px;
    height: 19px;
    background: #fff;
    transform: rotate(45deg);
`

export const PasswordInfoPopover = () => {
    const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null)
    const [anchorEl, setAnchorEl] = useState<SVGSVGElement | null>(null)
    const [open, setOpen] = useState(false)
    const { translate } = useTranslate()

    const handleClick = (event: React.MouseEvent<SVGSVGElement>) => {
        setOpen(!open)
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
        setOpen(false)
    }

    return (
        <>
            <ExclamationIcon
                onClick={handleClick}
                width={18}
                height={18}
                style={{ cursor: 'pointer', flexShrink: 0 }}
                data-testid="password-info-popover-exclamation-icon-handle-click"
            />
            <Popper
                sx={{
                    '@media (min-width: 1179px)': { bottom: '20px !important' },
                    bottom: '20px !important',
                    borderRadius: '8px !important',
                    border: 'none !important',
                    zIndex: 1300,
                    padding: '0 30px'
                }}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="top"
                transition
                keepMounted
                modifiers={[
                    { name: 'flip', enabled: false, options: { altBoundary: true, rootBoundary: 'viewport' } },
                    { name: 'preventOverflow', enabled: true, options: { altAxis: false, altBoundary: true, tether: true, rootBoundary: 'viewport' } },
                    { name: 'arrow', enabled: true, options: { element: arrowRef } }
                ]}
                data-testid="password-info-popover-popper">
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350} data-testid="password-info-popover-fade">
                        <Paper
                            sx={{
                                borderRadius: '8px !important',
                                width: { xs: 'auto', sm: '506px' },
                                padding: '20px 30px 30px 30px',
                                backgroundColor: '#fff',
                                boxShadow: '0px 4px 18px rgba(0, 0, 0, 0.05)',
                                boxSizing: 'border-box'
                            }}
                            data-testid="password-info-popover-paper-arrow">
                            <Arrow ref={setArrowRef} data-testid="password-info-popover-arrow">
                                <Triangle data-testid="password-info-popover-triangle" />
                            </Arrow>

                            <Box
                                onClick={handleClose}
                                sx={{ position: 'absolute', cursor: 'pointer', right: '40px' }}
                                data-testid="password-info-popover-box-handle-close">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-testid="password-info-popover-svg">
                                    <g opacity="0.5" data-testid="password-info-popover-g">
                                        <path d="M2 2L10 10" stroke="#003764" strokeWidth="1.5" data-testid="password-info-popover-path-1" />
                                        <path d="M10 2L2 10" stroke="#003764" strokeWidth="1.5" data-testid="password-info-popover-path-2" />
                                    </g>
                                </svg>
                            </Box>

                            <Box sx={{ mt: '20px' }} data-testid="password-info-popover-box-typography-your-password-must-contain">
                                <Typography
                                    variant="body2"
                                    sx={{ fontSize: { xs: '14px', sm: '16px' }, fontWeight: 700, color: '#0B3B61', mb: '12px' }}
                                    data-testid="password-info-popover-typography-your-password-must-contain">
                                    Your password must contain:
                                </Typography>
                                {DESCRIPTION_KEYS.map(item => (
                                    <Stack key={item} direction="row" gap="5px" data-testid="password-info-popover-stack-typography">
                                        <Typography
                                            variant="body2"
                                            sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 400, color: '#0B3B61', pl: '5px' }}
                                            data-testid="password-info-popover-typography">
                                            • {translate(item)}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Box>
                        </Paper>
                    </Fade>
                )}
            </Popper>
            <Backdrop
                sx={{ color: '#fff', zIndex: 1299, cursor: 'auto !important', background: 'rgba(56, 66, 80, 0.2)' }}
                open={open}
                onClick={handleClose}
                data-testid="password-info-popover-backdrop-handle-close"
            />
        </>
    )
}
